/**
 * Ajustes y cierre de sesión.
 *
 * El cierre de sesión BORRA todo lo del dispositivo, así que se rechaza
 * mientras quede algo sin sincronizar: un toque accidental en «salir» no puede
 * costar un día de campo. Se ofrece forzarlo sólo con confirmación explícita y
 * advirtiendo de forma inequívoca lo que se pierde.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useApp } from '../store';
import { ConnectionBadge } from './Home';

export function SettingsScreen() {
  const { operator, isOnline, pending, fieldPackage, packageFetchedAt, logout, loadFieldData } =
    useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState<number | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const doLogout = async (force: boolean) => {
    setBusy(true);
    try {
      const result = await logout(force);
      if (result.ok) {
        navigate('/', { replace: true });
      } else {
        setBlocked(result.pending ?? 0);
      }
    } finally {
      setBusy(false);
    }
  };

  const unsent = pending.interviews + pending.households + pending.rejected;

  return (
    <div className="app">
      <div className="topbar">
        <Link to="/" className="btn btn--ghost" style={{ minHeight: 'auto', padding: 4 }}>
          ‹ Inicio
        </Link>
        <div className="topbar__title">Ajustes</div>
        <ConnectionBadge isOnline={isOnline} />
      </div>

      <main className="app__main">
        <div className="card">
          <p className="card__title">{operator?.name}</p>
          <p className="card__meta">
            Código {operator?.code} · {operator?.role}
          </p>
          {operator?.birthdayRule ? (
            <p className="card__meta" style={{ marginTop: 8 }}>
              Tu regla de selección en la vivienda:{' '}
              <span className="strong">
                {operator.birthdayRule === 'ultimo'
                  ? 'último cumpleaños'
                  : 'próximo cumpleaños'}
              </span>
            </p>
          ) : null}
        </div>

        <div className="card">
          <p className="card__title">Cuestionario</p>
          {fieldPackage ? (
            <>
              <p className="card__meta">
                Versión {fieldPackage.version} · {fieldPackage.definition.blocks.length} bloques
              </p>
              <p className="card__meta">
                Metodología {fieldPackage.definition.methodologyVersion}
              </p>
              {fieldPackage.activeTrims.length > 0 ? (
                <p className="card__meta" style={{ marginTop: 8 }}>
                  Recortes activos: {fieldPackage.activeTrims.join(', ')}
                </p>
              ) : (
                <p className="card__meta" style={{ marginTop: 8 }}>
                  Sin recortes: se aplica el cuestionario completo.
                </p>
              )}
              {packageFetchedAt ? (
                <p className="faint" style={{ marginTop: 8 }}>
                  Descargado el{' '}
                  {new Date(packageFetchedAt).toLocaleString('es-MX', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              ) : null}
            </>
          ) : (
            <p className="card__meta">No descargado.</p>
          )}
          <button
            type="button"
            className="btn btn--secondary"
            style={{ marginTop: 12 }}
            onClick={() => void loadFieldData({ prefetchGeometry: true })}
            disabled={!isOnline}
          >
            Actualizar cuestionario y cartografía
          </button>
        </div>

        <div className="card">
          <p className="card__title">Datos en el dispositivo</p>
          <div className="stack" style={{ marginTop: 10 }}>
            <div className="row row--between">
              <span className="muted">Entrevistas sin enviar</span>
              <span className="strong">{pending.interviews}</span>
            </div>
            <div className="row row--between">
              <span className="muted">Viviendas sin enviar</span>
              <span className="strong">{pending.households}</span>
            </div>
            <div className="row row--between">
              <span className="muted">Rechazadas</span>
              <span className="strong">{pending.rejected}</span>
            </div>
            <div className="row row--between">
              <span className="muted">Entrevistas sin terminar</span>
              <span className="strong">{pending.drafts}</span>
            </div>
          </div>
          <Link to="/sincronizacion" className="btn btn--secondary" style={{ marginTop: 12 }}>
            Ver la cola
          </Link>
        </div>

        <div className="divider" />

        <h2 className="screen-title" style={{ fontSize: 18 }}>
          Cerrar sesión
        </h2>
        <p className="screen-sub">
          Al cerrar sesión se borra del teléfono todo lo capturado y el cuestionario descargado.
        </p>

        {unsent > 0 ? (
          <div className="notice notice--danger">
            <span className="notice__title">
              No cierres sesión: hay {unsent} registro(s) sin enviar
            </span>
            Si cierras ahora, ese trabajo de campo se pierde y no hay forma de recuperarlo.
            Conéctate y envíalo primero.
          </div>
        ) : null}

        {blocked !== null ? (
          <div className="notice notice--danger">
            <span className="notice__title">
              Quedan {blocked} registro(s) sin sincronizar
            </span>
            Para cerrar sesión de todos modos y PERDER ese trabajo, escribe{' '}
            <span className="mono strong">BORRAR</span> abajo.
            <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
              <input
                className="input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="BORRAR"
                autoCapitalize="characters"
                autoComplete="off"
              />
            </div>
            <button
              type="button"
              className="btn btn--danger btn--block"
              style={{ marginTop: 10 }}
              disabled={confirmText.trim().toUpperCase() !== 'BORRAR' || busy}
              onClick={() => void doLogout(true)}
            >
              Cerrar sesión y borrar los datos
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={`btn btn--block ${unsent > 0 ? 'btn--secondary' : 'btn--danger'}`}
            onClick={() => void doLogout(false)}
            disabled={busy}
          >
            {busy ? 'Cerrando…' : 'Cerrar sesión'}
          </button>
        )}

        <p className="faint center" style={{ marginTop: 24 }}>
          CAPI Tecate 2027 · Calimetría
        </p>
      </main>
    </div>
  );
}
